import keyBy from 'lodash/keyBy'
import qs from 'qs'
import {PROFILE_TABS, BRANDS} from 'const'

import config from 'config'
import {awsFetchRest} from 'utils/asyncUtils'
import {getLang} from 'utils/stringUtils'

const addressKeys = {
  [BRANDS.blick]: 'blickAddress',
  [BRANDS.energy]: 'energyAddress',
  [BRANDS.wirtschaftsmedien]: 'wimeGameAddress',
  [BRANDS.beobachter]: 'beoGameAddress',
  [BRANDS.moonandstars]: 'address_moonandstars',
  [BRANDS.vintage]: 'address_vintage',
  [BRANDS.schlager]: 'address_schlager',
  [BRANDS.rockit]: 'address_rockit',
  [BRANDS.twentymin]: 'address_20min',
  [BRANDS.lematin]: 'address_20min', // share 20min address with lematin
  [BRANDS.schweizerillustrierte]: 'schweizerillustrierteAddress',
  [BRANDS.lessentiel]: 'address_lessentiel',
  [BRANDS.autodesjahres]: 'addressAutodesjahres',
  [BRANDS.jobbern]: 'addressJobbern',
}

class ProfileUpdateService {
  constructor() {
    this.lang = getLang(window.location.search) || 'de'
  }

  getAuthHeader = token => `Bearer ${token}`

  static getAuthHeader = token => `Bearer ${token}`

  prepareData = (originalTabs, newTab) => {
    originalTabs = Object.values(originalTabs)
    const tabs = [{...originalTabs.find(tab => tab.tabName === newTab.tabName), ...newTab}]

    if (newTab.tabName === PROFILE_TABS.global) {
      tabs.push(...originalTabs.filter(tab => tab.tabName && tab.tabName !== PROFILE_TABS.global))
    } else {
      tabs.push(originalTabs.find(tab => tab.tabName === PROFILE_TABS.global))
    }

    return {tabs}
  }

  getUserProfile = async token => {
    const url = `${config.backendUrl}/user-profile`
    const response = await awsFetchRest(url, 'GET', undefined, {
      Authorization: this.getAuthHeader(token),
    })

    const tabs = keyBy(response.tabs, 'tabName')
    return {
      tabs,
      linkedIdentities: response.linkedIdentities,
      thirdPartyBrands: response.thirdPartyBrands,
      brandsWithAdmeira: response.brandsWithAdmeira,
      signedDataPrivacyAgreements: response.signedDataPrivacyAgreements,
    }
  }

  getUserProfileThirdPartiesData = async token => {
    const query = qs.stringify({lang: this.lang})
    const url = `${config.backendUrl}/user-profile-third-parties-data?${query}`
    const response = await awsFetchRest(url, 'GET', undefined, {
      Authorization: this.getAuthHeader(token),
    })

    return keyBy(response, 'tabName')
  }

  deleteThirdPartyBrand = async (token, auth0Id, brandName) =>
    awsFetchRest(
      `${config.backendUrl}/users/${encodeURIComponent(auth0Id)}/brands/${brandName}/third-party`,
      'DELETE',
      undefined,
      {
        Authorization: this.getAuthHeader(token),
      }
    )

  updateUserProfile = async (token, newData) => {
    const query = qs.stringify({lang: this.lang})
    const brandPath = newData.tabName !== 'global.profile' ? `/${newData.tabName}` : ''
    const url = `${config.backendUrl}/user-profile${brandPath}?${query}`
    await awsFetchRest(url, 'PUT', newData, {
      Authorization: this.getAuthHeader(token),
    })
  }

  getUserAddresses = async (token, auth0Id) =>
    awsFetchRest(`${config.backendUrl}/v2/users/${encodeURIComponent(auth0Id)}/addresses`, 'GET', undefined, {
      Authorization: this.getAuthHeader(token),
    })

  getUserAddress = async (token, auth0Id, addressId) =>
    awsFetchRest(
      `${config.backendUrl}/v2/users/${encodeURIComponent(auth0Id)}/addresses/${addressId}`,
      'GET',
      undefined,
      {
        Authorization: this.getAuthHeader(token),
      }
    )

  setAddressToBrand = async (token, auth0Id, addressId, brandName) => {
    const data = {
      addressId,
      addressKey: addressKeys[brandName],
    }
    return awsFetchRest(`${config.backendUrl}/v2/users/${encodeURIComponent(auth0Id)}/brandAddress`, 'PATCH', data, {
      Authorization: this.getAuthHeader(token),
    })
  }

  deleteUserAddress = async (token, auth0Id, addressId) =>
    awsFetchRest(
      `${config.backendUrl}/v2/users/${encodeURIComponent(auth0Id)}/addresses/${addressId}`,
      'DELETE',
      undefined,
      {
        Authorization: this.getAuthHeader(token),
      }
    )

  createUserAddress = async (token, auth0Id, address) =>
    awsFetchRest(`${config.backendUrl}/v2/users/${encodeURIComponent(auth0Id)}/addresses/`, 'POST', address, {
      Authorization: this.getAuthHeader(token),
    })

  updateUserAddress = async (token, auth0Id, id, address) =>
    awsFetchRest(`${config.backendUrl}/v2/users/${encodeURIComponent(auth0Id)}/addresses/${id}`, 'PUT', address, {
      Authorization: this.getAuthHeader(token),
    })

  setUserDefaultAddress = async (token, auth0Id, data) =>
    awsFetchRest(`${config.backendUrl}/v2/users/${encodeURIComponent(auth0Id)}/defaultAddress`, 'PATCH', data, {
      Authorization: this.getAuthHeader(token),
    })

  getUserCredentials = async token =>
    awsFetchRest(`${config.identityServiceUrl}/webauthn/credentials`, 'GET', undefined, {
      Authorization: this.getAuthHeader(token),
    })

  removeUserCredential = async (token, credentialId) =>
    awsFetchRest(`${config.identityServiceUrl}/webauthn/credentials/${credentialId}`, 'DELETE', undefined, {
      Authorization: this.getAuthHeader(token),
    })

  static removeIdentity = async (token, auth0Id, identity) => {
    await awsFetchRest(
      `${config.identityServiceUrl}/accounts/${encodeURIComponent(auth0Id)}/identities/${encodeURIComponent(identity)}`,
      'DELETE',
      undefined,
      {
        Authorization: this.getAuthHeader(token),
      }
    )
  }
}

export default ProfileUpdateService
